import React from 'react';

/**
 * Dimensioni (px) del frame di rendering e della pagina PDF della tessera digitale.
 * - PD: orizzontale (immagini digital_card_*_ANNO.png, 1016x638).
 * - GD: verticale (immagini digital_card_*_gd.png, aspetto 465x701 ≈ 0.66).
 * Condivise con ProfilePage (handleGeneratePDF) per tenere allineati frame e formato PDF.
 */
export const CARD_DIMENSIONS = {
  pd: { width: 1016, height: 638, orientation: 'landscape' },
  gd: { width: 638, height: 962, orientation: 'portrait' },
};

export const getCardDimensions = (gdCard) => (gdCard ? CARD_DIMENSIONS.gd : CARD_DIMENSIONS.pd);

/**
 * Testo che si adatta: parte da baseSize e riduce il font (fino a minSize) finché il valore
 * entra in maxWidthPx. Usato per circolo/federazione GD, gli unici valori che possono sforare;
 * i valori corti restano a baseSize.
 */
const AutoFitText = ({ text, maxWidthPx, baseSize = 30, minSize = 14 }) => {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState(baseSize);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !maxWidthPx) return;
    let current = baseSize;
    el.style.fontSize = `${current}px`;
    while (el.scrollWidth > maxWidthPx && current > minSize) {
      current -= 1;
      el.style.fontSize = `${current}px`;
    }
    setSize(current);
  }, [text, maxWidthPx, baseSize, minSize]);

  return (
    <span
      ref={ref}
      style={{
        fontSize: `${size}px`,
        // line-height fissa (indipendente dal font ridotto): il testo resta centrato
        // verticalmente sulla stessa riga e non "sale" quando rimpicciolisce.
        lineHeight: `${Math.round(baseSize * 1.2)}px`,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {text}
    </span>
  );
};

export const DigitalCard = ({
  campaignPrefix,
  name,
  nameAlias,
  surname,
  secretary,
  federation,
  entity,
  code,
  gdCard,
}) => {
  // Le tessere GD usano immagini base dedicate (suffisso "gd") e formato verticale.
  const baseSuffix = gdCard ? 'gd' : campaignPrefix;
  const { width, height } = getCardDimensions(gdCard);
  const displayName = nameAlias !== null ? nameAlias : name;

  const pageStyle = (image) => ({
    position: 'relative',
    width: `${width}px`,
    height: `${height}px`,
    backgroundImage: `url('/assets/images/digital_card/${image}')`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  });

  // Layout GD (verticale). Le etichette (NOME/COGNOME/CIRCOLO/FEDERAZIONE/CODICE TESSERA) sono
  // già nell'immagine e la firma della segretaria è stampata: qui scriviamo solo i valori sulle
  // righe tratteggiate. Coordinate in % del frame (prima taratura, da rifinire visivamente).
  // Il CODICE è su sfondo arancione → testo bianco; gli altri dentro riquadri bianchi → nero.
  const gdFields = [
    { top: 18, left: 55, value: code, color: '#ffffff' }, // CODICE TESSERA
    { top: 31, left: 30, value: displayName }, // NOME
    { top: 42, left: 38, value: surname }, // COGNOME
    { top: 54, left: 38, value: entity, autoFit: true, right: 85 }, // CIRCOLO
    { top: 64, left: 44, value: federation, autoFit: true, right: 85 }, // FEDERAZIONE
  ];

  return (
    <div id="pdf-frame">
      <div className="pdf-page" style={pageStyle(`digital_card_1_${baseSuffix}.png`)} />

      <div className="pdf-page" style={pageStyle(`digital_card_2_${baseSuffix}.png`)}>
        {gdCard ? (
          gdFields.map((f, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${f.top}%`,
                left: `${f.left}%`,
                fontWeight: 'bold',
                color: f.color || '#000000',
              }}
            >
              {f.autoFit ? (
                <AutoFitText text={f.value} maxWidthPx={(((f.right ?? 88) - f.left) / 100) * width} />
              ) : (
                <span style={{ fontSize: '30px' }}>{f.value}</span>
              )}
            </div>
          ))
        ) : (
          <>
            <div style={{ position: 'absolute', top: '25px', left: '70px', fontSize: '30px', fontWeight: 'bold' }}>
              <span>{secretary}</span>
            </div>
            <div style={{ position: 'absolute', top: '124px', left: '70px', fontSize: '30px', fontWeight: 'bold' }}>
              <span>{federation}</span>
            </div>

            <div style={{ position: 'absolute', top: '223px', left: '70px', fontSize: '30px', fontWeight: 'bold' }}>
              <span>{entity}</span>
            </div>

            <div style={{ position: 'absolute', top: '320px', left: '70px', fontSize: '30px', fontWeight: 'bold' }}>
              <span>
                {displayName} {surname}
              </span>
            </div>

            <div style={{ position: 'absolute', top: '422px', left: '70px', fontSize: '30px', fontWeight: 'bold' }}>
              <span>{code}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
